import React, { ChangeEvent, useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import "../../../styles/Login.scss";

interface HowToCategory {
    id: number;
    title: string;
    slug: string;
}

interface HowToItem {
    id: number;
    how_to_category_id: number;
    title: string;
    description: string;
    image: string | null;
    display_order: number;
    active: boolean;
}

interface HowToItemModalProps {
    item?: HowToItem | null;
    categories: HowToCategory[];
    onClose: () => void;
    onSaved: () => void;
}

const HowToItemModal: React.FC<HowToItemModalProps> = ({
                                                           item,
                                                           categories,
                                                           onClose,
                                                           onSaved,
                                                       }) => {
    const isEditing = !!item;

    const [categoryId, setCategoryId] = useState(
        item?.how_to_category_id
            ? String(item.how_to_category_id)
            : ""
    );

    const [title, setTitle] = useState(
        item?.title ?? ""
    );

    const [description, setDescription] = useState(
        item?.description ?? ""
    );

    const [displayOrder, setDisplayOrder] = useState(
        item?.display_order ?? 0
    );

    const [active, setActive] = useState(
        item?.active ?? true
    );

    const [newImage, setNewImage] =
        useState<File | null>(null);

    const [imagePreview, setImagePreview] = useState(
        item?.image
            ? `${IMAGE_URL}/${item.image}`
            : ""
    );

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        setCategoryId(
            item?.how_to_category_id
                ? String(item.how_to_category_id)
                : ""
        );

        setTitle(item?.title ?? "");
        setDescription(item?.description ?? "");
        setDisplayOrder(item?.display_order ?? 0);
        setActive(item?.active ?? true);
        setNewImage(null);

        setImagePreview(
            item?.image
                ? `${IMAGE_URL}/${item.image}`
                : ""
        );
    }, [item]);

    const handleImageChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0] ?? null;

        setNewImage(file);

        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            setImagePreview(
                item?.image
                    ? `${IMAGE_URL}/${item.image}`
                    : ""
            );
        }
    };

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!categoryId) {
            setError("Please select a category.");
            return;
        }

        if (!title.trim()) {
            setError("Title is required.");
            return;
        }

        if (!description.trim()) {
            setError("Description is required.");
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();

            formData.append(
                "how_to_category_id",
                categoryId
            );

            formData.append(
                "title",
                title.trim()
            );

            formData.append(
                "description",
                description
            );

            formData.append(
                "display_order",
                String(displayOrder)
            );

            formData.append(
                "active",
                active ? "1" : "0"
            );

            if (newImage) {
                formData.append("image", newImage);
            }

            let response;

            if (isEditing && item) {
                formData.append("_method", "PUT");

                response = await api.post(
                    `/admin/how-to-items/${item.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );
            } else {
                response = await api.post(
                    "/admin/how-to-items",
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );
            }



            setSuccess(
                isEditing
                    ? "How To item updated successfully."
                    : "How To item created successfully."
            );

            onSaved();
        } catch (err: any) {
            console.error(
                "Failed to save How To item:",
                err
            );

            if (err.response?.data?.errors) {
                const validationErrors =
                    err.response.data.errors;

                const firstError =
                    Object.values(validationErrors)[0];

                if (Array.isArray(firstError)) {
                    setError(String(firstError[0]));
                } else {
                    setError(String(firstError));
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save How To item."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal admin-how-to-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit How To"
                            : "Add How To"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="form-success">
                            {success}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="how-to-category">
                            Category
                        </label>

                        <select
                            id="how-to-category"
                            value={categoryId}
                            onChange={(e) =>
                                setCategoryId(
                                    e.target.value
                                )
                            }
                            disabled={loading}
                        >
                            <option value="">
                                Select a category
                            </option>

                            {categories
                                .map((category) => (
                                    <option
                                        key={category.id}
                                        value={category.id}
                                    >
                                        {category.title}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="how-to-title">
                            Title
                        </label>

                        <input
                            id="how-to-title"
                            type="text"
                            value={title}
                            onChange={(e) =>
                                setTitle(e.target.value)
                            }
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="how-to-description">
                            Description
                        </label>

                        <textarea
                            id="how-to-description"
                            value={description}
                            onChange={(e) =>
                                setDescription(
                                    e.target.value
                                )
                            }
                            rows={8}
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="how-to-image">
                            Image
                        </label>

                        {imagePreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={imagePreview}
                                    alt={title || "How To"}
                                />
                            </div>
                        )}

                        <input
                            id="how-to-image"
                            type="file"
                            accept="image/*"
                            onChange={
                                handleImageChange
                            }
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="how-to-display-order">
                            Display Order
                        </label>

                        <input
                            id="how-to-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(
                                        e.target.checked
                                    )
                                }
                                disabled={loading}
                            />

                            <span>Active</span>
                        </label>
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowToItemModal;
